import { useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import type { TTinyCustomer } from '@/core/types'
import { fetchCustomerById } from '@/core/services/customer.service'
import { UInput } from '@/core/components/ui'
import { useTranslate } from '@repo/unicity'
import { isCustomerEligibleToPurchase } from './ticket.utils'

interface CustomerIdModalProps {
    open: boolean
    onClose: () => void
    onSubmit: (customer: TTinyCustomer) => void
}

const CustomerIdModal = ({ open, onClose, onSubmit }: CustomerIdModalProps) => {
    const [customerId, setCustomerId] = useState('')
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const { translate } = useTranslate()

    const handleSubmit = async () => {
        const trimmedValue = customerId.trim()
        if (!trimmedValue) {
            setError(translate('please_enter_customer_id') || 'Please enter your Customer ID')
            return
        }
        const numericId = Number(trimmedValue)
        if (isNaN(numericId) || numericId <= 0) {
            setError(translate('invalid_customer_id') || 'Please enter a valid Customer ID')
            return
        }

        setIsLoading(true)
        setError('')

        try {
            const customer = await fetchCustomerById(numericId)

            // Check if customer is eligible to purchase (only A, B, M allowed)
            if (!isCustomerEligibleToPurchase(customer)) {
                setError(
                    translate('customer_not_eligible') ||
                        'Your account status does not allow purchasing. Only Active (A), Pending (B), or Member (M) status customers can purchase.'
                )
                setIsLoading(false)
                return
            }

            // Customer is eligible, proceed with submission
            onSubmit(customer)
            setCustomerId('')
            setError('')
        } catch (err) {
            setError(translate('customer_not_found') || 'Customer not found. Please check your ID and try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        setCustomerId('')
        setError('')
        onClose()
    }

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="customer-id-modal">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'calc(100% - 48px)',
                    maxWidth: 400,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: '48px 24px',
                    backgroundColor: 'background1.light'
                }}>
                <Stack gap={4}>
                    {/* Title Section */}
                    <Stack alignItems="center" gap={1}>
                        <Typography
                            variant="body2"
                            color="primary.main"
                            textAlign="center"
                            sx={{
                                opacity: isLoading ? 0.5 : 1
                            }}>
                            {translate('glic2027_please_sign_in')}
                        </Typography>
                    </Stack>

                    {/* Input Box */}
                    <UInput
                        fullWidth
                        placeholder={translate('glic2027_unicity_distributor_id')}
                        value={customerId}
                        onChange={e => {
                            if (/^[0-9]*$/.test(e.target.value) || e.target.value === '') {
                                setCustomerId(e.target.value)
                            }
                            if (error) setError('')
                        }}
                        onKeyDown={e => {
                            // Submit on Enter, same gate as the Continue button (> 4 chars)
                            if (e.key === 'Enter' && customerId.trim().length > 4 && !isLoading) {
                                handleSubmit()
                            }
                        }}
                        onClear={() => setCustomerId('')}
                        disabled={isLoading}
                        slotProps={{
                            input: {
                                className: 'no-shrink',
                                sx: {
                                    '& ::placeholder': {
                                        color: customerId.length > 0 ? 'rgba(0, 59, 111, 1)' : 'rgba(112, 119, 131, 1)!important'
                                    }
                                }
                            },
                            htmlInput: {
                                autoFocus: true
                            }
                        }}
                        type="tel"
                        autoComplete="new-password"
                    />
                    {/* Error Alert */}
                    {error && (
                        <Alert icon={false} severity="error" sx={{ bgcolor: 'rgba(255, 231, 235, 1)', justifyContent: 'center' }}>
                            <Typography color="error" variant="body2" textAlign="center">
                                {error}
                            </Typography>
                        </Alert>
                    )}
                    {/* Buttons */}
                    <Stack gap={2} alignItems="center">
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={handleSubmit}
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            disabled={customerId.trim().length <= 4 || isLoading}
                            sx={{
                                '&:disabled': {
                                    backgroundColor: 'secondary.light',
                                    color: 'secondary.main'
                                },
                                textTransform: 'uppercase',
                                minHeight: '65px'
                            }}>
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : translate('continue')}
                        </Button>
                        <Typography
                            variant="link"
                            color="primary.main"
                            onClick={handleClose}
                            sx={{ cursor: 'pointer', textDecoration: 'none', textTransform: 'uppercase' }}>
                            {translate('cancel')}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Modal>
    )
}

export default CustomerIdModal
